import React, { useState, useCallback, useEffect, useMemo } from 'react';

// Grid de fotos/videos del Camino agrupadas por galería.
// Cada galería se muestra como una sección con su título, fecha y línea divisoria,
// seguida de un grid propio con las fotos de esa galería.
// El lightbox es global: navega entre todos los medios cronológicamente,
// mostrando en la caption a qué galería pertenece cada uno.
const CaminoGalleryGrid = ({ galleries = [] }) => {
  const [lightboxIndex, setLightboxIndex] = useState(null);

  // Aplanamos para el lightbox (necesita índice lineal), pero preservamos
  // el keyId para vincular con la posición dentro de su galería.
  const flatItems = useMemo(() => (
    galleries.flatMap(g =>
      (Array.isArray(g.media) ? g.media : []).map((m, idx) => ({
        ...m,
        galleryId: g.id,
        galleryTitle: g.title,
        galleryDate: g.date,
        keyId: `${g.id}-${idx}`
      }))
    )
  ), [galleries]);

  // Índice de inicio de cada galería dentro de flatItems, para saber a qué
  // posición absoluta corresponde el ítem clicado.
  const groupOffsets = useMemo(() => {
    const offsets = [];
    let cur = 0;
    galleries.forEach(g => {
      const n = Array.isArray(g.media) ? g.media.length : 0;
      offsets.push(cur);
      cur += n;
    });
    return offsets;
  }, [galleries]);

  const openLightbox = (idx) => setLightboxIndex(idx);
  const closeLightbox = useCallback(() => setLightboxIndex(null), []);

  const goPrev = useCallback(() => {
    setLightboxIndex(i => (i === null ? null : (i - 1 + flatItems.length) % flatItems.length));
  }, [flatItems.length]);

  const goNext = useCallback(() => {
    setLightboxIndex(i => (i === null ? null : (i + 1) % flatItems.length));
  }, [flatItems.length]);

  useEffect(() => {
    if (lightboxIndex === null) return;
    const handler = (e) => {
      if (e.key === 'Escape') closeLightbox();
      else if (e.key === 'ArrowLeft') goPrev();
      else if (e.key === 'ArrowRight') goNext();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [lightboxIndex, closeLightbox, goPrev, goNext]);

  const galleriesConMedia = galleries.filter(g => Array.isArray(g.media) && g.media.length > 0);

  if (galleriesConMedia.length === 0) {
    return (
      <div className="text-center text-gray-500 py-10 px-5">
        <div className="text-5xl mb-3" aria-hidden="true">📸</div>
        <p>Aún no hay fotos del Camino. Volvé pronto.</p>
      </div>
    );
  }

  const current = lightboxIndex !== null ? flatItems[lightboxIndex] : null;

  const formatDate = (d) => {
    if (!d) return '';
    try {
      return new Date(d).toLocaleDateString('es-ES', {
        day: 'numeric', month: 'long', year: 'numeric'
      });
    } catch { return ''; }
  };

  return (
    <>
      <div className="px-1">
        {galleriesConMedia.map((g, gIdx) => {
          const media = Array.isArray(g.media) ? g.media : [];
          const offset = groupOffsets[galleries.indexOf(g)];
          return (
            <section key={g.id || gIdx} className="mb-8">
              {/* Cabecera de galería */}
              <div className="px-3 mb-3">
                <div className="flex items-baseline justify-between gap-3 mb-1">
                  <h3 className="text-lg font-bold text-gray-800 leading-tight">
                    {g.title || 'Galería del Camino'}
                  </h3>
                  {g.date && (
                    <span className="text-xs text-gray-500 whitespace-nowrap flex-shrink-0">
                      {formatDate(g.date)}
                    </span>
                  )}
                </div>
                <div
                  aria-hidden="true"
                  style={{
                    height: 2,
                    background: 'linear-gradient(90deg, #e62c19 0%, #a01916 40%, transparent 100%)',
                    borderRadius: 2
                  }}
                />
              </div>

              {/* Grid de esta galería */}
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 px-3">
                {media.map((it, idx) => {
                  const absIdx = offset + idx;
                  const withCtx = { ...it, galleryTitle: g.title };
                  return (
                    <button
                      key={`${g.id}-${idx}`}
                      onClick={() => openLightbox(absIdx)}
                      className="relative aspect-square bg-gray-200 rounded-lg overflow-hidden focus:outline-none focus:ring-2 focus:ring-yellow-500"
                      aria-label={withCtx.caption || g.title || 'Foto del Camino'}
                    >
                      {it.type === 'video' ? (
                        <>
                          {it.thumbnail ? (
                            <img
                              src={it.thumbnail}
                              alt={it.caption || ''}
                              className="w-full h-full object-cover"
                              loading="lazy"
                            />
                          ) : (
                            <div className="w-full h-full bg-gray-300 flex items-center justify-center">
                              <span className="text-3xl">🎬</span>
                            </div>
                          )}
                          <div className="absolute inset-0 flex items-center justify-center bg-black/20">
                            <div className="bg-white/90 rounded-full w-12 h-12 flex items-center justify-center text-2xl">
                              ▶
                            </div>
                          </div>
                        </>
                      ) : (
                        <img
                          src={it.url}
                          alt={it.caption || ''}
                          className="w-full h-full object-cover"
                          loading="lazy"
                        />
                      )}
                    </button>
                  );
                })}
              </div>
            </section>
          );
        })}
      </div>

      {/* Lightbox */}
      {current && (
        <div
          className="fixed inset-0 z-50 bg-black/90 flex flex-col"
          onClick={closeLightbox}
        >
          <div className="flex justify-end p-4">
            <button
              onClick={closeLightbox}
              className="text-white text-3xl w-12 h-12 rounded-full bg-white/10 hover:bg-white/20"
              aria-label="Cerrar"
            >
              ×
            </button>
          </div>

          <div
            className="flex-1 flex items-center justify-center px-4 pb-4"
            onClick={(e) => e.stopPropagation()}
          >
            {current.type === 'video' ? (
              <video
                src={current.url}
                controls
                autoPlay
                playsInline
                className="max-h-full max-w-full"
                style={{ maxHeight: '80vh' }}
              />
            ) : (
              <img
                src={current.url}
                alt={current.caption || ''}
                className="max-h-full max-w-full object-contain"
                style={{ maxHeight: '80vh' }}
              />
            )}
          </div>

          <div
            className="text-white px-6 py-4 flex items-center justify-between gap-4"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={goPrev}
              className="text-3xl w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 flex-shrink-0"
              aria-label="Anterior"
            >
              ‹
            </button>
            <div className="text-center text-sm flex-1">
              {current.caption && (
                <div className="font-medium mb-1">{current.caption}</div>
              )}
              <div className="opacity-70 text-xs">
                {current.galleryTitle}
                {' · '}
                {lightboxIndex + 1} / {flatItems.length}
              </div>
            </div>
            <button
              onClick={goNext}
              className="text-3xl w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 flex-shrink-0"
              aria-label="Siguiente"
            >
              ›
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default CaminoGalleryGrid;
